import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Alert, Button, Code, Group, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Catches render-time errors in the page content so a single screen's bug shows an
 * inline fallback (with the message) instead of unmounting the whole app to a blank
 * page. Reset it on navigation by giving it a `key` that changes per route, so moving
 * to another screen always mounts a fresh boundary.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Screen render error:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <Alert
        color="red"
        variant="light"
        icon={<IconAlertTriangle size={18} />}
        title="This screen hit a render error"
      >
        <Stack gap="sm">
          <Text size="sm">
            The rest of the console still works — use the sidebar to navigate elsewhere, or
            reload. Details:
          </Text>
          <Code block>{error.message}</Code>
          <Group>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          </Group>
        </Stack>
      </Alert>
    );
  }
}
